import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    // Add this new code
    const selectedOption = window.themeGlobals?.pageLoadSelectedOption;
    //console.log(`pageLoadSelectedOption ${selectedOption}`)
    if (selectedOption) {
      this.#showMediaForOption(selectedOption);
    }

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

     

   target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // NEW CODE: Listen for color selection changes
    target?.addEventListener('color:selected', this.#handleColorSelected, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    //this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  /**
 * Shows only the media items that match the selected option.
 * @param {string} selectedOption - The selected variant option (e.g., "Black", "Asphalt")
 */
 #showMediaForOption = (selectedOption) => {
  console.log('=== FILTERING FOR:', selectedOption, '===');
  
  const mediaItems = this.refs.media;
  const slideshowSlides = this.querySelectorAll('slideshow-slide');
  
  const hasMatchingAlt = (element) => {
    const img = element.querySelector('img');
    const alt = img?.getAttribute('alt');
    return alt === selectedOption;
  };
  
  let hasMatchingImage = false;
  
  if (mediaItems && mediaItems.length > 0) {
    mediaItems.forEach((mediaItem) => {
      if (hasMatchingAlt(mediaItem)) {
        hasMatchingImage = true;
      }
    });
  }
  
  if (!hasMatchingImage && slideshowSlides.length > 0) {
    slideshowSlides.forEach((slide) => {
      if (hasMatchingAlt(slide)) {
        hasMatchingImage = true;
      }
    });
  }
  
  const paginationDots = this.querySelectorAll('.slideshow-controls__dots li');
  
  // If no matches found, show all (fallback)
  if (!hasMatchingImage) {
    console.log('FALLBACK: Showing all images');
    mediaItems?.forEach((item) => { item.style.display = ''; });
    slideshowSlides.forEach((slide) => { slide.style.display = ''; });
    paginationDots.forEach((dot) => { dot.style.display = ''; });
    return;
  }
  
  // Filter desktop gallery
  if (mediaItems && mediaItems.length > 0) {
    mediaItems.forEach((mediaItem) => {
      const shouldShow = hasMatchingAlt(mediaItem);
      mediaItem.style.display = shouldShow ? '' : 'none';
    });
  }
  
  // Track the first visible dot
  let firstVisibleDotIndex = -1;
  
  // Filter mobile slideshow AND corresponding pagination dots
  if (slideshowSlides.length > 0) {
    slideshowSlides.forEach((slide, index) => {
      const shouldShow = hasMatchingAlt(slide);
      slide.style.display = shouldShow ? '' : 'none';
      
      // Track first visible index
      if (shouldShow && firstVisibleDotIndex === -1) {
        firstVisibleDotIndex = index;
      }
      
      // Hide/show the corresponding pagination dot
      if (paginationDots[index]) {
        paginationDots[index].style.display = shouldShow ? '' : 'none';
      }
    });
  }
  
  // Update aria-selected for all pagination buttons
  paginationDots.forEach((dot, index) => {
    const button = dot.querySelector('button');
    if (button) {
      // Set aria-selected="true" only for the first visible dot
      button.setAttribute('aria-selected', index === firstVisibleDotIndex ? 'true' : 'false');
    }
  });
  
  console.log('First visible dot index:', firstVisibleDotIndex);
  console.log('=== FILTERING COMPLETE ===');
};

    /**
   * Handles the color:selected event from variant picker.
   * @param {CustomEvent} event - The color selected event.
   */
  #handleColorSelected = (event) => {
    const selectedColor = event.detail.color;
    //console.log('Media gallery received color:', selectedColor);
    
    if (selectedColor) {
      this.#showMediaForOption(selectedColor);
    }
  };
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
